
import Checkbox from '@marketplace/components/forms/inputs/checkbox.vue'
import TextInput from '@marketplace/components/forms/inputs/text-input.vue'
import GermanLogos from '@marketplace/components/logos/GermanLogos.vue'
import {
    LOGIN_MUTATION,
    MAGIC_LINK_MUTATION,
    RECOVER_MUTATION,
    VERIFY_LOGIN_CODE_MUTATION,
} from '@marketplace/graphql/auth.js'
import { unlocalizeRouteName } from '@marketplace/helpers/router.js'
import { trackLoggedOut } from '@marketplace/helpers/segment/index.js'
import { EventBus } from '@marketplace/plugins/event-bus.js'
import Cookies from 'js-cookie'
import { email, minLength, required } from 'vuelidate/lib/validators'

export default {
    name: 'ModalLogin',

    components: {
        Checkbox,
        TextInput,
        GermanLogos,
    },

    data: () => ({
        remember: true,
        email: null,
        password: null,
        code: null,
        errors: null,
        userErrors: null,
        isLoading: false,
        isVerifying: false,
        isSendingMagicLink: false,
        hasSentMagicLink: false,
        hasSentPasswordReset: false,
        awaitingCode: false,
        hasResentCode: false,
    }),

    computed: {},

    validations: {
        email: {
            email,
            required,
        },
        password: {
            minLength: minLength(5),
            required,
        },
    },

    mounted() {
        // Force focus on the email input, just the autofocus attribute isn't reliable enough.
        this.$refs.emailInput.focus()

        trackLoggedOut('Login - Open Modal')
    },

    methods: {
        async login() {
            this.resetErrors()
            this.$v.email.$touch()
            this.$v.password.$touch()

            if (this.$v.$invalid) {
                return
            }
            this.isLoading = true

            const { remember, email, password } = this.$data
            await this.$apollo
                .mutate({
                    mutation: LOGIN_MUTATION,
                    variables: {
                        remember,
                        email,
                        password,
                    },
                })
                .then((res) => {
                    this.isLoading = false

                    const { account, user, userErrors, requiresVerification } =
                        res.data.login

                    if (requiresVerification) {
                        this.awaitingCode = true

                        return
                    }

                    if (account && user) {
                        this.handleAuthenticated(user)
                    } else {
                        this.userErrors = userErrors
                    }
                })
        },

        async verifyCode() {
            this.resetErrors()

            if (!this.code) {
                this.userErrors = [
                    {
                        field: ['code'],
                        message: this.$t('validation.required'),
                    },
                ]

                return
            }

            this.isVerifying = true

            const { remember, email, code } = this.$data

            await this.$apollo
                .mutate({
                    mutation: VERIFY_LOGIN_CODE_MUTATION,
                    variables: {
                        remember,
                        email,
                        code,
                    },
                })
                .then((res) => {
                    this.isVerifying = false

                    const { account, user, userErrors } =
                        res.data.verifyLoginCode

                    if (account && user) {
                        this.handleAuthenticated(user)
                    } else {
                        this.userErrors = userErrors
                    }
                })
                .catch(() => {
                    this.isVerifying = false
                })
        },

        async resendCode() {
            await this.login()

            this.hasResentCode = true
        },

        handleAuthenticated(user) {
            EventBus.$emit('modal-close')
            const isFollowBrandInUrl = Cookies.get('followBrand')

            // Check for returnUrl and navigate accordingly
            const urlParams = new URLSearchParams(window.location.search)
            const returnUrl = urlParams.get('returnUrl')

            if (returnUrl) {
                window.location.href = this.localeUrl(
                    decodeURIComponent(returnUrl)
                )
            } else if (isFollowBrandInUrl) {
                window.location.href = `${this.localeUrl(
                    window.location.pathname
                )}?follow-brand=true`
                Cookies.remove('followBrand')
            } else {
                if (
                    unlocalizeRouteName(this.$route.name) === 'home' &&
                    user.lastVisitedEnvironment === 'dropshipping'
                ) {
                    window.location.href = this.localePath({
                        name: 'ds',
                    })

                    return
                }

                const newPath =
                    window.location.pathname + window.location.search
                window.location.href = this.localeUrl(newPath)
            }
        },

        async sendMagicLink() {
            this.resetErrors()

            this.$v.email.$touch()

            if (this.$v.email.$invalid) {
                return
            }

            this.isSendingMagicLink = true

            await this.$apollo
                .mutate({
                    mutation: MAGIC_LINK_MUTATION,
                    variables: {
                        email: this.email,
                    },
                })
                .then((res) => {
                    this.isSendingMagicLink = false
                    this.hasSentMagicLink = true
                })
                .catch(() => {
                    this.isSendingMagicLink = false
                    this.userErrors = [
                        {
                            field: [],
                            message: this.$t(
                                'messages.could_not_send_magic_link'
                            ),
                        },
                    ]
                })
        },

        async resetPassword() {
            this.resetErrors()

            if (!this.email) {
                this.userErrors = [
                    {
                        field: [],
                        message: this.$t('messages.please_enter_email'),
                    },
                ]
                return
            }

            this.isLoading = true

            await this.$apollo
                .mutate({
                    mutation: RECOVER_MUTATION,
                    variables: {
                        email: this.email,
                    },
                })
                .then((res) => {
                    this.hasSentPasswordReset = true
                    this.isLoading = false
                })
        },

        resetErrors() {
            this.$v.$reset()
            this.userErrors = null
            this.hasSentMagicLink = false
            this.hasSentPasswordReset = false
            this.hasResentCode = false
        },
        handleChange(payload) {
            this.remember = payload.value
        },
    },
}
